import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Trophy, Ticket, Clock, Users, Flame, History as HistoryIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import TelegramShareButton from '../components/TelegramShareButton';

const API = process.env.REACT_APP_BACKEND_URL;

function Countdown({ seconds }) {
    const [remaining, setRemaining] = useState(Math.max(0, seconds));
    useEffect(() => {
        setRemaining(Math.max(0, seconds));
        const i = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
        return () => clearInterval(i);
    }, [seconds]);

    const d = Math.floor(remaining / 86400);
    const h = Math.floor((remaining % 86400) / 3600);
    const m = Math.floor((remaining % 3600) / 60);
    const s = remaining % 60;

    return (
        <div className="flex gap-2" data-testid="jackpot-countdown">
            {[['J', d], ['H', h], ['M', m], ['S', s]].map(([label, val]) => (
                <div key={label} className="bg-black/40 border border-[#FFD700]/30 rounded-lg px-3 py-2 min-w-[60px] text-center">
                    <div className="text-2xl font-bold text-[#FFD700]">{String(val).padStart(2, '0')}</div>
                    <div className="text-xs text-white/50">{label}</div>
                </div>
            ))}
        </div>
    );
}

export default function JackpotPage() {
    const { t } = useTranslation();
    const { address } = useWeb3();
    const [pool, setPool] = useState(null);
    const [stats, setStats] = useState(null);
    const [tickets, setTickets] = useState(null);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        const load = async () => {
            try {
                const [p, s, h] = await Promise.all([
                    fetch(`${API}/api/jackpot/current`).then(r => r.json()),
                    fetch(`${API}/api/jackpot/stats`).then(r => r.json()),
                    fetch(`${API}/api/jackpot/history?limit=10`).then(r => r.json()),
                ]);
                setPool(p); setStats(s); setHistory(h.history || []);
                if (address) {
                    const t = await fetch(`${API}/api/jackpot/tickets/${address}`).then(r => r.json());
                    setTickets(t);
                }
            } catch (e) { console.error(e); }
        };
        load();
        const i = setInterval(load, 15000);
        return () => clearInterval(i);
    }, [address]);

    return (
        <div className="min-h-screen" data-testid="jackpot-page">
            <PageHero
                icon={Trophy}
                title={t('jackpot.title')}
                highlight={t('jackpot.highlight')}
                subtitle={t('jackpot.subtitle')}
                right={
                    <TelegramShareButton
                        title={t('jackpot.shareTitle', { eth: pool?.prize_pool_eth?.toFixed(4) || '0' })}
                        subtitle={t('jackpot.shareSubtitle', { count: pool?.ticket_count || 0 })}
                        path="/jackpot"
                        tone="gold"
                        size="md"
                        label={t('jackpot.share')}
                        testId="share-jackpot-btn"
                    />
                }
            />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

                {/* Current Pool */}
                <Card className="bg-gradient-to-br from-[#FFD700]/10 via-black/60 to-[#FF6B00]/10 border-[#FFD700]/30 mb-8 overflow-hidden relative">
                    <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, #FFD700, transparent 50%)' }} />
                    <CardContent className="p-8 relative">
                        <div className="flex flex-wrap items-end justify-between gap-6">
                            <div>
                                <div className="text-sm text-white/60 uppercase tracking-widest">{t('jackpot.potInPlay')}</div>
                                <div className="text-6xl font-black text-[#FFD700] my-2" data-testid="jackpot-prize">
                                    {pool?.prize_pool_eth?.toFixed(6) ?? '0.000000'} <span className="text-2xl">ETH</span>
                                </div>
                                <div className="text-sm text-white/50">{t('jackpot.week')} {pool?.week_key ?? '—'}</div>
                            </div>
                            <div>
                                <div className="text-sm text-white/60 uppercase tracking-widest mb-2">{t('jackpot.drawIn')}</div>
                                {pool && <Countdown seconds={pool.seconds_to_draw} />}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
                            <div className="bg-black/30 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-white/60 text-sm"><Ticket className="w-4 h-4" />{t('jackpot.totalTickets')}</div>
                                <div className="text-2xl font-bold text-white">{pool?.ticket_count ?? 0}</div>
                            </div>
                            <div className="bg-black/30 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-white/60 text-sm"><Users className="w-4 h-4" />{t('jackpot.contributions')}</div>
                                <div className="text-2xl font-bold text-white">{pool?.contributions ?? 0}</div>
                            </div>
                            <div className="bg-black/30 rounded-lg p-4">
                                <div className="flex items-center gap-2 text-white/60 text-sm"><Flame className="w-4 h-4 text-[#FF6B00]" />{t('jackpot.lifetimeDistributed')}</div>
                                <div className="text-2xl font-bold text-[#FF6B00]">{stats?.all_time_distributed_eth?.toFixed(4) ?? '0.0000'} ETH</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* My Tickets */}
                {address && tickets && (
                    <Card className="bg-white/5 border-white/10 mb-8">
                        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Ticket className="w-5 h-5 text-[#00FF66]" />{t('jackpot.myTicketsTitle')}</CardTitle></CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-6 items-center">
                                <div>
                                    <div className="text-4xl font-bold text-[#00FF66]" data-testid="my-tickets-count">{tickets.my_tickets}</div>
                                    <div className="text-sm text-white/60">{t('jackpot.myTickets')}</div>
                                </div>
                                <div className="flex-1 min-w-[200px]">
                                    <div className="text-sm text-white/60 mb-1">{t('jackpot.winProbability')}</div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 h-3 bg-white/10 rounded-full overflow-hidden">
                                            <div className="h-full bg-gradient-to-r from-[#00FF66] to-[#FFD700]" style={{ width: `${Math.min(100, tickets.win_probability_percent)}%` }} />
                                        </div>
                                        <div className="text-[#FFD700] font-bold">{tickets.win_probability_percent}%</div>
                                    </div>
                                    <div className="text-xs text-white/40 mt-1">{t('jackpot.moreBetsHint')}</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* History */}
                <Card className="bg-white/5 border-white/10">
                    <CardHeader><CardTitle className="text-white flex items-center gap-2"><HistoryIcon className="w-5 h-5" />{t('jackpot.drawHistory')}</CardTitle></CardHeader>
                    <CardContent>
                        {history.length === 0 ? (
                            <div className="text-center text-white/40 py-8">{t('jackpot.noDraws')}</div>
                        ) : (
                            <div className="space-y-2">
                                {history.map((h, idx) => (
                                    <div key={h.week_key || `draw-${idx}`} className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-white/5" data-testid={`jackpot-history-${idx}`}>
                                        <div>
                                            <div className="text-white font-mono text-sm">{h.winner?.slice(0, 10)}...{h.winner?.slice(-4)}</div>
                                            <div className="text-xs text-white/40">{t('jackpot.weekShort')} {h.week_key} · {t('jackpot.ticketsCount', { count: h.ticket_count })}</div>
                                        </div>
                                        <Badge className="bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/30">+{h.prize_eth?.toFixed(6)} ETH</Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <div className="mt-8 p-4 bg-white/5 rounded-lg border border-white/10 text-sm text-white/60 flex items-center gap-3">
                    <Clock className="w-4 h-4 text-[#00FF66]" />
                    <span>{t('jackpot.verifyHint')}</span>
                </div>
            </div>
        </div>
    );
}
